import { Howl } from 'howler';

export interface SoundSettings {
  enabled: boolean;
  volume: number;
  musicEnabled?: boolean;
  musicVolume?: number;
  effectsVolume?: number;
  effectsEnabled?: boolean; // new: allow disabling effects while keeping music
  hapticsEnabled?: boolean;
  trackId?: string;
}

class SoundManager {
  private settings: SoundSettings;
  private audioContext: AudioContext | null = null;
  private musicEl: HTMLAudioElement | null = null;
  // Music playlist: put corresponding audio files into public/sounds/
  // Naming convention: <id>.mp3 (optionally also <id>.ogg for broader codec coverage)
  // Example files you should provide:
  // public/sounds/bg.mp3, public/sounds/chill.mp3, public/sounds/arcade.mp3
  // To add more, push another object with a unique id & readable title and supply the matching file.
  private playlist: { id: string; title: string; src: string }[] = [
    { id: 'bg', title: 'Klassisk', src: '/sounds/bg.mp3' },
    { id: 'chill', title: 'Chill', src: '/sounds/chill.mp3' },
    { id: 'arcade', title: 'Arcade', src: '/sounds/arcade.mp3' },
  ];
  private currentTrackId: string = 'bg';
  private diceRollHowls: Howl[] | null = null;

  constructor() {
    this.settings = this.loadSettings();
    this.initAudioContext();
  }

  private loadSettings(): SoundSettings {
    try {
      const stored = localStorage.getItem('yatzy-sound-settings');
  const base = stored ? JSON.parse(stored) : { enabled: true, volume: 0.7 };
  const merged = { musicEnabled: true, musicVolume: 0.4, effectsVolume: base.volume ?? 0.7, effectsEnabled: true, hapticsEnabled: true, ...base } as SoundSettings;
      if (merged.trackId && typeof merged.trackId === 'string') this.currentTrackId = merged.trackId;
      return merged;
    } catch {
  return { enabled: true, volume: 0.7, musicEnabled: true, musicVolume: 0.4, effectsVolume: 0.7, effectsEnabled: true, hapticsEnabled: true, trackId: 'bg' };
    }
  }

  private saveSettings(): void {
    try {
      localStorage.setItem('yatzy-sound-settings', JSON.stringify(this.settings));
    } catch (error) {
      console.error('Error saving sound settings:', error);
    }
  }

  private initAudioContext(): void {
    try {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    } catch (error) {
      console.warn('Web Audio API not supported:', error);
    }
  }

  private ensureMusicEl(): HTMLAudioElement | null {
    if (!this.musicEl) {
      try {
        const src = this.getCurrentTrack().src;
        this.musicEl = new Audio(src);
        this.musicEl.loop = true;
        this.musicEl.preload = 'auto';
        this.musicEl.volume = (this.settings.musicVolume ?? 0.4) * (this.settings.enabled ? 1 : 0);
      } catch (e) {
        console.warn('Music element init failed', e);
        this.musicEl = null;
      }
    }
    return this.musicEl;
  }

  private async playTone(frequency: number, duration: number, type: OscillatorType = 'sine'): Promise<void> {
    if (!this.settings.enabled || !this.audioContext) return;
    // Some test environments stub AudioContext without full API; bail quietly if createOscillator missing
    if (typeof (this.audioContext as any).createOscillator !== 'function' || typeof (this.audioContext as any).createGain !== 'function') {
      return;
    }
    try {
      const oscillator = this.audioContext.createOscillator();
      const gainNode = this.audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext.destination);

      oscillator.frequency.setValueAtTime(frequency, this.audioContext.currentTime);
      oscillator.type = type;

      gainNode.gain.setValueAtTime(0, this.audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(this.settings.volume * 0.3, this.audioContext.currentTime + 0.01);
      gainNode.gain.exponentialRampToValueAtTime(0.001, this.audioContext.currentTime + duration);

      oscillator.start(this.audioContext.currentTime);
      oscillator.stop(this.audioContext.currentTime + duration);
    } catch (error) {
      // Swallow in production too—audio is non-critical; avoid noisy console in tests
    }
  }

  private async playChord(frequencies: number[], duration: number): Promise<void> {
    const promises = frequencies.map(freq => this.playTone(freq, duration));
    await Promise.all(promises);
  }

  // Specifika ljudeffekter
  async playDiceRoll(): Promise<void> {
    if (!this.settings.enabled || this.settings.effectsEnabled === false) return;
    // Lazy init sample sounds
    if (!this.diceRollHowls) {
      try {
        this.diceRollHowls = ['dice-roll-1.mp3', 'dice-roll-2.mp3', 'dice-roll-3.mp3']
          .map(f => new Howl({ src: [`/sounds/${f}`], volume: (((this.settings.effectsVolume ?? this.settings.volume) ?? 0.7) * 0.9) }));
      } catch (e) {
        this.diceRollHowls = [];
      }
    }
    const pool = this.diceRollHowls || [];
    if (pool.length > 0) {
      const sound = pool[Math.floor(Math.random() * pool.length)];
      try { sound.play(); return; } catch {}
    }
    // Fallback to synthesized tones if samples missing
    const frequencies = [180, 260, 220, 300, 240];
    for (let i = 0; i < frequencies.length; i++) {
      setTimeout(() => this.playTone(frequencies[i], 0.09, 'square'), i * 42);
    }
  }

  async playDiceLock(): Promise<void> {
    if (!this.settings.enabled || this.settings.effectsEnabled === false) return;
    await this.playTone(800, 0.15, 'sine');
  }

  async playDiceUnlock(): Promise<void> {
    if (!this.settings.enabled || this.settings.effectsEnabled === false) return;
    await this.playTone(600, 0.15, 'sine');
  }

  async playScoreSelect(): Promise<void> {
    if (!this.settings.enabled || this.settings.effectsEnabled === false) return;
    await this.playChord([523, 659, 784], 0.3); // C-E-G ackord
  }

  async playYatzy(): Promise<void> {
    if (!this.settings.enabled || this.settings.effectsEnabled === false) return;
    // Fanfar för Yatzy!
    const melody = [523, 659, 784, 1047]; // C-E-G-C oktav
    for (let i = 0; i < melody.length; i++) {
      setTimeout(() => this.playTone(melody[i], 0.4), i * 200);
    }
  }

  async playSuperYatzy(): Promise<void> {
    if (!this.settings.enabled || this.settings.effectsEnabled === false) return;
    // Extra pampig fanfar för 6 lika i Maxi
    const melody = [659, 784, 988, 1175, 1319];
    melody.forEach((f, i) => setTimeout(()=> this.playTone(f, 0.45), i*180));
    // Glitter avslut
    setTimeout(()=> this.playChord([1568, 1760, 1976], 0.5), melody.length*180 + 80);
  }

  async playGameComplete(): Promise<void> {
    if (!this.settings.enabled || this.settings.effectsEnabled === false) return;
    // Triumf-melodi
    const melody = [392, 523, 659, 784, 1047];
    for (let i = 0; i < melody.length; i++) {
      setTimeout(() => this.playTone(melody[i], 0.5), i * 150);
    }
  }

  async playGameStart(): Promise<void> {
    if (!this.settings.enabled || this.settings.effectsEnabled === false) return;
    // Starttoner för nytt spel
    await this.playChord([523, 659, 784], 0.4); // C-E-G
  }

  async playAchievement(): Promise<void> {
    if (!this.settings.enabled || this.settings.effectsEnabled === false) return;
    // Belönings-ljud
    await this.playChord([659, 831, 988], 0.6); // E-G#-B
  }

  async playHighScore(): Promise<void> {
    if (!this.settings.enabled || this.settings.effectsEnabled === false) return;
    // Extra speciellt ljud för ny highscore
    const melody = [1047, 1175, 1319, 1568]; // Hög fanfar
    for (let i = 0; i < melody.length; i++) {
      setTimeout(() => this.playTone(melody[i], 0.3), i * 100);
    }
  }

  async playButtonClick(): Promise<void> {
    if (!this.settings.enabled || this.settings.effectsEnabled === false) return;
    await this.playTone(1000, 0.1, 'square');
  }

  async playError(): Promise<void> {
    if (!this.settings.enabled || this.settings.effectsEnabled === false) return;
    await this.playTone(200, 0.3, 'sawtooth');
  }

  // Public simple tone for timers/ticks
  async playTick(frequency: number = 800, duration: number = 0.08): Promise<void> {
    await this.playTone(frequency, duration, 'square');
  }

  // Settings
  setEnabled(enabled: boolean): void {
    this.settings.enabled = enabled;
    this.saveSettings();
    // Also gate music
    if (!enabled) {
      this.pauseMusic();
    } else if (this.settings.musicEnabled) {
      this.playMusic().catch(() => {});
    }
  }

  setVolume(volume: number): void {
    this.settings.volume = Math.max(0, Math.min(1, volume));
    this.saveSettings();
  }

  setEffectsVolume(volume: number): void {
    this.settings.effectsVolume = Math.max(0, Math.min(1, volume));
    // update loaded howls volume
    if (this.diceRollHowls) {
      this.diceRollHowls.forEach(h => h.volume((this.settings.effectsVolume ?? 0.7) * 0.9));
    }
    this.saveSettings();
  }

  getEffectsVolume(): number { return this.settings.effectsVolume ?? this.settings.volume; }
  setEffectsEnabled(enabled: boolean) { this.settings.effectsEnabled = enabled; this.saveSettings(); }
  getEffectsEnabled(): boolean { return this.settings.effectsEnabled !== false; }

  setHapticsEnabled(enabled: boolean) { this.settings.hapticsEnabled = enabled; this.saveSettings(); }
  getHapticsEnabled(): boolean { return this.settings.hapticsEnabled !== false; }

  getSettings(): SoundSettings {
    return { ...this.settings };
  }

  // Resume audio context (krävs för många browsers)
  async resumeAudioContext(): Promise<void> {
    if (this.audioContext && this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }
  }

  // Background music controls
  async playMusic(): Promise<void> {
    if (!this.settings.enabled || !this.settings.musicEnabled) return;
    const el = this.ensureMusicEl();
    if (!el) return;
    try {
      el.volume = this.settings.musicVolume ?? 0.4;
      await el.play();
    } catch (e) {
      // Autoplay might be blocked until user gesture
      console.debug('Music play deferred until user interaction');
    }
  }

  pauseMusic(): void {
    const el = this.musicEl;
    if (el && !el.paused) {
      try { el.pause(); } catch {}
    }
  }

  setMusicEnabled(enabled: boolean): void {
    this.settings.musicEnabled = enabled;
    this.saveSettings();
    if (enabled) {
      this.playMusic().catch(() => {});
    } else {
      this.pauseMusic();
    }
  }

  setMusicVolume(vol: number): void {
    this.settings.musicVolume = Math.max(0, Math.min(1, vol));
    this.saveSettings();
    if (this.musicEl) this.musicEl.volume = this.settings.musicVolume ?? 0.4;
  }

  getPlaylist() {
    return this.playlist.slice();
  }

  getCurrentTrack() {
    const found = this.playlist.find(t => t.id === this.currentTrackId);
    return found || this.playlist[0];
  }

  async setTrack(trackId: string): Promise<void> {
    if (this.currentTrackId === trackId) return;
    const previous = this.musicEl;
    const wasPlaying = !!previous && !previous.paused;
    this.currentTrackId = trackId;
  this.settings.trackId = trackId;
  this.saveSettings();

    // Prepare new element (lazy) if we are going to play
    if (wasPlaying || (this.settings.enabled && this.settings.musicEnabled)) {
      const nextEl = new Audio(this.getCurrentTrack().src);
      nextEl.loop = true;
      nextEl.preload = 'auto';
      nextEl.volume = 0; // start silent for crossfade
      this.musicEl = nextEl;
      try {
        await nextEl.play();
      } catch (e) {
        console.debug('Autoplay blocked for new track; will start on user gesture');
      }
      // Crossfade 600ms
      const fadeMs = 600;
      const start = performance.now();
      const targetVol = this.settings.musicVolume ?? 0.4;
      const prevStartVol = previous ? previous.volume : 0;
      const tick = (t: number) => {
        const ratio = Math.min(1, (t - start) / fadeMs);
        if (previous) previous.volume = prevStartVol * (1 - ratio);
        if (this.musicEl === nextEl) nextEl.volume = targetVol * ratio;
        if (ratio < 1) requestAnimationFrame(tick); else {
          if (previous) {
            try { previous.pause(); } catch {}
          }
        }
      };
      requestAnimationFrame(tick);
    } else {
      // Not playing now, just reset element so next playMusic() uses new source
      this.musicEl = null;
    }
  }

  toggleMusic(): void {
    this.setMusicEnabled(!(this.settings.musicEnabled));
  }
}

export const soundManager = new SoundManager();
